/**
 * useAssociatedRecordsPicker — ricerca server-paginata multi-type per il
 * picker "Associa record" di Task.
 *
 * Aggrega N entity types (Person + Business + eventuali plugin) in un
 * unico stream paginato: l'utente digita una query, ogni tipo viene
 * cercato in parallelo via l'endpoint `relationships/{groupId}/contributor-records`
 * (la ricerca è delegata al RelationshipContributor lato server, risolto sul
 * className FQN), i risultati sono uniti in una lista piatta con
 * `typeName`/`fqn`/`id`/`label` pronti per Autocomplete MUI
 * (`groupBy` + `getOptionLabel`).
 *
 * Il load-more carica la pagina successiva di *ogni* tipo contemporaneamente
 * e appende al totale. Non perfetto se uno dei tipi ha molti piu' record
 * degli altri (la distribuzione pageSize per-type e' fissa), ma semplice e
 * sufficiente per i picker del CRM (Person/Business/Quotation tipicamente
 * di ordini di grandezza comparabili).
 *
 * Opzionalmente la ricerca si restringe a un singolo tipo via `activeTypeName`
 * (vedi le opzioni): il pannello "Associazioni", quando l'utente seleziona una
 * tipologia, cerca solo su quella evitando le fetch inutili agli altri tipi;
 * nella vista "Selezionate" disattiva del tutto la ricerca server-side.
 */

import { useCallback } from 'react';
import { useServerPagedSearch } from '@crm/shared';
import { getAllEntityTypes } from '../entityRegistry/entityRegistry';
import { relationshipApi } from '../entities/relationship/relationshipApi';

export interface AssociatedRecord {
  typeName: string;
  fqn: string;
  id: number;
  label: string;
  /**
   * Testo secondario opzionale (es. l'email della Person, già nel formato
   * "<indirizzo>"). Deriva da ContributorRelationship.description; i widget che
   * non lo mostrano lo ignorano. Usato dal selettore "Contattati" (TS-56).
   */
  subtitle?: string;
}

interface UseAssociatedRecordsPickerOptions {
  /** Type names associabili (es. ['Person', 'Business', 'Quotation']). */
  associableTypeNames: readonly string[];
  /** GroupId corrente dal Redux store. */
  groupId: number;
  /** Dimensione pagina complessiva (divisa tra i type). Default 20. */
  pageSize?: number;
  /**
   * Restringe la ricerca a un singolo tipo. Tri-stato:
   *  - `undefined` → ricerca su TUTTI gli `associableTypeNames` (default; es.
   *    il picker multi-type always-open dei form di create);
   *  - `null`      → nessuna ricerca (es. vista "Selezionate" del pannello, che
   *    filtra i record già associati lato client: nessuna fetch al server);
   *  - `<typeName>` → ricerca SOLO su quel tipo (vista a tipo attivo nel
   *    pannello), evitando le chiamate inutili agli altri tipi.
   */
  activeTypeName?: string | null;
}

export function useAssociatedRecordsPicker(options: UseAssociatedRecordsPickerOptions) {
  const { associableTypeNames, groupId, pageSize = 20, activeTypeName } = options;

  const fetchPage = useCallback(
    async (query: string, page: number, size: number) => {
      // null → vista "Selezionate": nessuna ricerca server-side.
      if (activeTypeName === null) return { items: [] as AssociatedRecord[], totalCount: 0 };

      // <typeName> → solo quel tipo; undefined → tutti i tipi associabili.
      const scopedTypeNames =
        activeTypeName !== undefined ? [activeTypeName] : associableTypeNames;

      const all = getAllEntityTypes();
      const types = scopedTypeNames
        .map((tn) => all.find((d) => d.typeName === tn))
        .filter((d): d is NonNullable<typeof d> => !!d && !!d.fqn);
      if (types.length === 0) return { items: [] as AssociatedRecord[], totalCount: 0 };

      // Dividiamo il budget pagina tra i type, con minimo 5 per type per
      // evitare pagine degenere con 1-2 risultati ciascuno.
      const perType = Math.max(5, Math.floor(size / types.length));

      // La ricerca passa per il RelationshipContributor lato server: una GET
      // per tipo su /contributor-records (risolta sul className FQN), non più
      // il fan-out per-entità su <entity>Api.getAll. I risultati arrivano già
      // come ContributorRelationship (classPK + title), pronti da mappare.
      const results = await Promise.all(
        types.map(async (desc) => {
          try {
            const p = await relationshipApi.getContributorRecords(
              groupId,
              desc.fqn,
              query,
              page,
              perType
            );
            const mapped: AssociatedRecord[] = p.items.map((item) => ({
              typeName: desc.typeName,
              fqn: desc.fqn,
              id: item.classPK,
              label: item.title,
              subtitle: item.description ?? undefined,
            }));
            return { items: mapped, totalCount: p.totalCount };
          } catch {
            return { items: [] as AssociatedRecord[], totalCount: 0 };
          }
        })
      );

      return {
        items: results.flatMap((r) => r.items),
        totalCount: results.reduce((sum, r) => sum + r.totalCount, 0),
      };
    },
    [associableTypeNames, groupId, activeTypeName]
  );

  return useServerPagedSearch<AssociatedRecord>({
    fetchPage,
    pageSize,
    // activeTypeName in resetOn: cambiare tipo attivo azzera e rilancia la
    // ricerca da pagina 1 (niente option stantie del tipo precedente).
    resetOn: [associableTypeNames.join(','), groupId, activeTypeName ?? ''],
    // null → nessuna fetch; <type> → groupId valido; undefined → almeno un tipo.
    enabled:
      groupId > 0 &&
      activeTypeName !== null &&
      (activeTypeName !== undefined || associableTypeNames.length > 0),
  });
}
